import { Component, type ErrorInfo, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button as UiButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Loading, empty and error states.
 *
 * These exist as shared primitives from day one because demos die in the states
 * nobody thought about, and an empty dashboard on a projector reads as a
 * feature that does not work. Every screen in this app uses these rather than
 * inventing its own.
 */

/**
 * A skeleton shaped like the content it replaces, so the layout does not jump
 * when data lands.
 *
 * Both halves are the registry now: a stock `<Card>` holding two stock
 * `<Skeleton>`s. The card is what keeps the box from moving when the data
 * arrives — same border, same radius, same shadow the real content lands in —
 * and the only classes on it are the padding the real cards use, because the
 * registry card's own `py-6`/`gap-6` are built for header/content slots a
 * placeholder does not have.
 *
 * The pulse is the skeleton's, not the card's: pulsing the whole card faded its
 * border in and out, which reads as the page still deciding whether it exists.
 */
export function Loading({
  rows = 3,
  label = "Loading",
}: {
  rows?: number;
  label?: string;
}) {
  return (
    <div className="space-y-3" role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label}…</span>
      {Array.from({ length: rows }).map((_, index) => (
        <Card key={index} className="gap-3 p-4">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-3 w-3/5" />
        </Card>
      ))}
    </div>
  );
}

/**
 * An empty state that says what to do next, not merely that there is nothing
 * here. "No requests yet" is a dead end; "No open requests — everything has
 * been ordered" tells the reader the system is working.
 *
 * The next step is a sentence, not a button. It carried an `action` slot for a
 * call-to-action under the copy and no screen ever filled it — the screens with
 * somewhere to send the reader put that control in the page heading, where it
 * is in the same place whether the list is empty or not.
 */
export function Empty({
  title,
  body,
  tone = "neutral",
}: {
  title: string;
  body?: string;
  tone?: "neutral" | "good";
}) {
  return (
    <div
      /* Stock card geometry with a dashed edge — the registry's own convention
         for "a container with nothing in it yet". `good` keeps the green,
         because green is the status language's and an empty list that is
         *good news* is the one case that is not merely absence. */
      className={cn(
        "rounded-xl border border-dashed px-6 py-10 text-center",
        tone === "good" ? "border-green-300 bg-green-50/60" : "bg-card",
      )}
    >
      <p
        className={cn(
          "font-semibold",
          tone === "good" ? "text-green-900" : "text-foreground",
        )}
      >
        {title}
      </p>
      {body ? (
        <p
          className={cn(
            "mx-auto mt-1.5 max-w-sm text-sm",
            tone === "good" ? "text-green-800" : "text-muted-foreground",
          )}
        >
          {body}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A failure the user can act on: what broke, and the one button worth
 * pressing. Never a bare stack trace.
 *
 * `title` is required. It defaulted to "Something went wrong", which is the
 * exact sentence this component exists to keep off a projector — and every
 * caller already names the thing that failed, so the default was only ever a
 * way for a future one not to.
 */
export function ErrorState({
  title,
  body,
  onRetry,
}: {
  title: string;
  body?: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      /* A card with a 4px left rule — the same grammar as every card and tile
         in the app. Border rather than ring now, which is what the registry
         draws an edge with. */
      className="rounded-xl border border-red-200 border-l-4 border-l-red-500 bg-red-50 px-6 py-5 text-center"
    >
      <p className="font-semibold text-red-900">{title}</p>
      {body ? (
        <p className="mx-auto mt-1.5 max-w-md text-sm text-red-800">{body}</p>
      ) : null}
      {onRetry ? (
        /* The registry `Button`, not the kit's, and not because the kit's
           `secondary` is wrong — it is exactly this button. `kit.tsx` imports
           `errorMessage` from this file, so reaching back into it from here
           would close an import cycle around the module that every screen's
           error path runs through. Composing on the registry costs one import
           and no cycle.

           Stock `outline`, entirely: the retry sits *inside* a card that is
           already red and already says what broke, so a second red fill on the
           one control in it adds no information and takes the row's only
           hierarchy away. Margin is the only class. */
        <UiButton
          type="button"
          variant="outline"
          onClick={onRetry}
          className="mt-5"
        >
          Try again
        </UiButton>
      ) : null}
    </div>
  );
}

/** Shown when the app cannot start at all. Deliberately not styled like the
 *  rest of the app — it must be unmistakable that this is a setup problem, not
 *  a data one. */
export function ConfigError({ missing }: { missing: string[] }) {
  return (
    <div className="mx-auto max-w-md p-6 font-sans">
      <div className="rounded-xl border-2 border-red-400 bg-red-50 p-6">
        <h1 className="text-lg font-bold text-red-900">Nexora cannot start</h1>
        <p className="mt-2 text-sm text-red-900">
          {missing.length === 1
            ? "A required environment variable is missing:"
            : "Required environment variables are missing:"}
        </p>
        <ul className="mt-3 space-y-1">
          {missing.map((name) => (
            <li key={name} className="font-mono text-sm text-red-950">
              {name}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-red-800">
          Set these in <code className="font-mono">.env.local</code> for local
          development, or in Cloudflare Pages' environment variables for the
          deployed site, then rebuild. See <code>docs/SETUP.md</code>.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Failure                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * A Convex error message a person can read.
 *
 * Convex appends `[Request ID: …]` to every server error. It is useful in a log
 * and meaningless on a projector, so it is stripped, and the remainder is
 * truncated — a stack trace that overflows the card is the same failure as a
 * blank screen, just longer.
 */
export function cleanConvexError(err: unknown, max = 200): string {
  const message = err instanceof Error ? err.message : String(err ?? "");
  return message.replace(/\[Request ID: [^\]]+\]\s*/, "").slice(0, max);
}

/**
 * The one useful line out of a Convex error, for a toast description.
 *
 * The server's refusals are written to be read by the person who hit them —
 * "Dee is the only active owner", "Cannot receive more than was sent" — and
 * they arrive wrapped in a "Server Error" banner, a request id and a stack.
 * Fifteen call sites printed `error.message` raw, so the wrapper is what the
 * client would have seen on a projector instead of the sentence that tells
 * them what to do next.
 *
 * Untruncated on the way in: the 200-character cut belongs at the end, once
 * the one line worth showing has been picked out of the stack. `undefined` for
 * anything that is not an `Error`, because a toast with no description reads
 * better than one describing an object.
 */
export const errorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof Error)) return undefined;
  const lines = cleanConvexError(error, Infinity)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const thrown =
    lines.find((line) => /\w*Error:\s/.test(line) && !/^Server Error/.test(line)) ??
    lines[0] ??
    "";
  return (
    thrown
      .replace(/^.*?\w*Error:\s*/, "")
      .replace(/\s+at handler.*$/, "")
      .slice(0, 200) || undefined
  );
};

/**
 * One screen failing must not take the page down.
 *
 * Convex's `useQuery` **throws** on a server error — it never returns null, so
 * a `data === null` branch does not catch it. Without a boundary per screen, a
 * refused query escapes to the root boundary and blanks the whole page, live,
 * in front of the client. A class is required: React has no hook equivalent for
 * `componentDidCatch`.
 *
 * Retry is the caller's decision because the two useful answers are different.
 * A screen that owns an attempt counter remounts its subtree — there is nothing
 * to re-run otherwise, since Convex throws during render. A screen that does
 * not falls back to reloading the page.
 */
export class ScreenBoundary extends Component<
  {
    children: ReactNode;
    /** The error card's heading: "Inventory could not load". */
    title: string;
    /** Rendered above the card, so a failed screen still says which screen. */
    heading?: ReactNode;
    /** Wraps heading and card. The reports need a `<section id data-report>`
     *  the harness can select; everything else takes the default. */
    wrap?: (body: ReactNode) => ReactNode;
    /** Defaults to a full page reload. */
    onRetry?: () => void;
    /** A console breadcrumb for whoever is debugging. No stack trace reaches
     *  the screen either way. */
    onError?: (error: Error, info: ErrorInfo) => void;
  },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { title, heading, wrap, onRetry } = this.props;
    const body = (
      <>
        {heading}
        <ErrorState
          title={title}
          body={cleanConvexError(error)}
          onRetry={onRetry ?? (() => window.location.reload())}
        />
      </>
    );

    return wrap ? (
      wrap(body)
    ) : (
      <div data-screen-state="error" className="space-y-4">
        {body}
      </div>
    );
  }
}
